import React, { useState } from 'react';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { useAnalytics } from '@/hooks/useAnalytics';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  UserCheck, 
  Sparkles, 
  Trophy, 
  Star,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProfileCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({
  isOpen,
  onClose
}) => {
  const { missingFields, updateProfile, isLoading } = useProfileCompletion();
  const { trackEvent } = useAnalytics();
  const [formData, setFormData] = useState({
    name: '',
    nickname: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (formData.name.trim().length < 2) {
      setError('Nome deve ter pelo menos 2 caracteres');
      return false;
    }
    if (!formData.nickname.trim()) {
      setError('Nickname é obrigatório');
      return false;
    }
    if (formData.nickname.trim().length < 2) {
      setError('Nickname deve ter pelo menos 2 caracteres');
      return false;
    }
    if (formData.nickname.trim().length > 50) {
      setError('Nickname deve ter no máximo 50 caracteres');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const success = await updateProfile(formData.name, formData.nickname);
      
      if (success) {
        trackEvent('profile_completed', {
          category: 'user_profile',
          label: 'Profile completion modal',
          custom_parameters: {
            fields_completed: missingFields.join(','),
            name_length: formData.name.length,
            nickname_length: formData.nickname.length
          }
        });

        onClose();
      } else {
        setError('Erro ao salvar perfil. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      setError('Erro ao salvar perfil. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMissingFieldsText = () => {
    if (missingFields.includes('name') && missingFields.includes('nickname')) {
      return 'nome e nickname';
    } else if (missingFields.includes('name')) {
      return 'nome';
    } else if (missingFields.includes('nickname')) {
      return 'nickname';
    }
    return 'informações';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
              <UserCheck className="h-5 w-5 text-white" />
            </div>
            Complete seu perfil
          </DialogTitle>
          <DialogDescription className="text-base">
            Para uma experiência completa na plataforma, precisamos de algumas informações básicas.
          </DialogDescription>
        </DialogHeader>

        <Card className="border-0 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-4 w-4 text-blue-600" />
              Por que completar o perfil?
            </CardTitle>
            <CardDescription>
              Seu perfil personalizado melhora sua experiência na plataforma
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Trophy className="h-4 w-4 text-yellow-600" />
              <span>Apareça no ranking com seu nome real</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Star className="h-4 w-4 text-blue-600" />
              <span>Personalize sua experiência de gamificação</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <User className="h-4 w-4 text-green-600" />
              <span>Identifique-se facilmente na comunidade</span>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              Nome completo *
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Digite seu nome completo"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={cn(
                "transition-colors",
                error && "border-red-500 focus:border-red-500"
              )}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="nickname" className="text-sm font-medium">
              Nickname *
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                @
              </span>
              <Input
                id="nickname"
                type="text"
                placeholder="seu_nickname"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                className={cn(
                  "pl-8 transition-colors",
                  error && "border-red-500 focus:border-red-500"
                )}
                disabled={isSubmitting}
              />
            </div>
            <p className="text-xs text-gray-500">
              Será exibido no ranking e gamificação
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-600">{error}</span>
            </div>
          )}

          <div className="flex items-center justify-between pt-4">
            <Badge variant="outline" className="text-xs">
              <CheckCircle className="h-3 w-3 mr-1" />
              {missingFields.length} campo(s) obrigatório(s)
            </Badge>
            
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Depois
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !formData.name.trim() || !formData.nickname.trim()}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {isSubmitting ? 'Salvando...' : 'Completar perfil'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionModal;
